import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Save,
  Loader2,
  Eye,
  RotateCcw,
} from "lucide-react";
import DOMPurify from "dompurify";
import { usersApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import SignatureEditor from "../ui/SignatureEditor";

const EmailSignaturePage: React.FC = () => {
  const navigate = useNavigate();
  const { userData, firmData, refreshUserData } = useUserDataContext();

  const buildDefaultSignature = () => {
    const name = userData.name || "Your Name";
    const title = userData.title || "";
    const firm = firmData.name || "";
    const phone = userData.phone || (firmData as any).primaryPhone || "";
    const lines = [
      `<strong style="font-size: 14px; color: #0F5132;">${name}</strong>`,
      title ? `<span style="color: #475569; font-size: 12px;">${title}</span>` : "",
      firm ? `<span style="color: #64748b; font-size: 12px;">${firm}</span>` : "",
      phone ? `<span style="color: #64748b; font-size: 12px;">${phone}</span>` : "",
    ].filter(Boolean);
    return `<div style="font-family: 'Helvetica Neue', Arial, sans-serif; line-height: 1.5; border-left: 2px solid #0F5132; padding-left: 12px;">${lines.join("<br/>")}</div><div style="margin-top: 8px; font-size: 10px; color: #94a3b8; font-style: italic;">This email and any attachments are privileged and confidential.</div>`;
  };

  // If saved value is JSON (old format), treat as empty
  const getSavedSignature = (): string => {
    if (!userData.signatureBlock) return "";
    try { JSON.parse(userData.signatureBlock); return ""; } catch { /* HTML */ }
    return userData.signatureBlock;
  };

  const initial = getSavedSignature() || buildDefaultSignature();
  const [signatureHtml, setSignatureHtml] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await usersApi.update(userData._id, {
        "profile.signatureBlock": signatureHtml,
      });
      await refreshUserData();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Failed to save signature:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSignatureHtml(buildDefaultSignature());
  };

  const previewHtml = DOMPurify.sanitize(signatureHtml, {
    ADD_TAGS: ["style"],
    ADD_ATTR: ["style", "target", "cellpadding", "cellspacing"],
  });

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/settings")}
          className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">Email Signature</h1>
          <p className="text-sm text-slate-500 mt-1">
            Customize the signature appended to your outgoing emails
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: saved ? "#16a34a" : "var(--color-primary)" }}
          onMouseEnter={(e) => {
            if (!saved) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = saved ? "#16a34a" : "var(--color-primary)";
          }}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saved ? (
            <>
              <Save className="h-4 w-4" />
              Saved
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Signature
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor Panel */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
            <span className="text-sm font-semibold text-slate-700">Edit Signature</span>
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              title="Reset to default signature"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </button>
          </div>
          <SignatureEditor
            value={signatureHtml}
            onChange={setSignatureHtml}
          />
        </motion.div>

        {/* Preview Panel */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
            <Eye className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">Preview in Email</span>
          </div>
          <div className="p-5">
            <div className="border border-slate-200 rounded-xl p-5 bg-white">
              {/* Simulated email body above signature */}
              <div className="text-sm text-slate-600 mb-4 pb-4 border-b border-slate-100">
                <p>Hi John,</p>
                <p className="mt-2">Thanks for your email. I'll review the documents and get back to you by end of day.</p>
                <p className="mt-2">Best regards,</p>
              </div>
              {/* Signature */}
              <div
                className="email-signature-preview"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tips */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-6 bg-slate-50 rounded-xl border border-slate-200 px-5 py-4"
      >
        <p className="text-sm font-semibold text-slate-700 mb-2">Tips</p>
        <ul className="text-xs text-slate-500 space-y-1.5">
          <li>Click directly into the signature to edit text  -  use the toolbar to format</li>
          <li>Keep your signature concise for a professional look</li>
          <li>You can toggle the signature on/off per email in the compose area</li>
          <li>Click <strong>Reset</strong> to restore the default signature with your profile info</li>
        </ul>
      </motion.div>
    </div>
  );
};

export default EmailSignaturePage;
